import React from 'react';
interface FieldShellProps {
  id: string;
  label: string;
  error?: string;
  optional?: boolean;
  hint?: string;
  children: React.ReactNode;
}

/** Shared label + error shell so every field on the flow reads identically. */
export const FieldShell = ({
  id,
  label,
  error,
  optional,
  hint,
  children
}: FieldShellProps) => <div data-id="element-861" data-mp="1zr80ml7ceays">
    <label htmlFor={id} className="block font-body text-sm sm:text-[0.95rem] font-medium text-tns-charcoal mb-2 leading-relaxed" data-id="element-862" data-mp="1zqy9uveh3zs1">
      {label}
      {optional && <span className="ml-2 font-normal text-tns-charcoal-soft text-xs uppercase tracking-wider" data-id="element-863" data-mp="28p0fjo95jgln">
          Optional
        </span>}
    </label>
    {hint && <p className="font-body text-sm text-tns-charcoal-soft mb-2.5 leading-relaxed" data-id="element-864" data-mp="2igo1j9mxw5fq">
        {hint}
      </p>}
    {children}
    {error && <p id={`${id}-error`} role="alert" className="mt-2 font-body text-sm text-[#9B3B2F]" data-id="element-865" data-mp="2nh2bix83ppge">
        {error}
      </p>}
  </div>;
const inputBase = 'w-full font-body text-tns-charcoal bg-white border rounded-lg px-4 py-3 transition-colors duration-200 placeholder:text-tns-charcoal-soft/60 focus:outline-none focus:ring-2 focus:ring-tns-gold/50 focus:border-tns-gold';
const borderFor = (error?: string) => error ? 'border-[#C98B7F]' : 'border-tns-ivory-dark';
interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'email' | 'tel';
  placeholder?: string;
  error?: string;
  optional?: boolean;
  autoComplete?: string;
}
export const TextField = ({
  id,
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  error,
  optional,
  autoComplete
}: TextFieldProps) => <FieldShell id={id} label={label} error={error} optional={optional} data-id="element-866" data-mp="3nstyfheb2oge">
    <input id={id} name={id} type={type} value={value} placeholder={placeholder} autoComplete={autoComplete} onChange={e => onChange(e.target.value)} aria-invalid={!!error} aria-describedby={error ? `${id}-error` : undefined} className={`${inputBase} ${borderFor(error)}`} data-id="element-867" data-mp="o63qoxjod1nn" />
  </FieldShell>;
interface TextAreaFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  rows?: number;
}
export const TextAreaField = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  error,
  rows = 5
}: TextAreaFieldProps) => <FieldShell id={id} label={label} error={error} data-id="element-868" data-mp="1uyualuasscks">
    <textarea id={id} name={id} value={value} rows={rows} placeholder={placeholder} onChange={e => onChange(e.target.value)} aria-invalid={!!error} aria-describedby={error ? `${id}-error` : undefined} className={`${inputBase} ${borderFor(error)} resize-y leading-relaxed min-h-[120px]`} data-id="element-869" data-mp="1zwce3561pzle" />
  </FieldShell>;
interface SelectFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: {
    value: string;
    label: string;
  }[];
  placeholder?: string;
  error?: string;
  disabled?: boolean;
}
export const SelectField = ({
  id,
  label,
  value,
  onChange,
  options,
  placeholder = 'Select…',
  error,
  disabled
}: SelectFieldProps) => <FieldShell id={id} label={label} error={error} data-id="element-870" data-mp="1ifozk8h2gyjc">
    <select id={id} name={id} value={value} disabled={disabled} onChange={e => onChange(e.target.value)} aria-invalid={!!error} aria-describedby={error ? `${id}-error` : undefined} className={`${inputBase} ${borderFor(error)} appearance-none bg-[right_1rem_center] bg-no-repeat pr-11 disabled:opacity-60`} style={{
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%232E2E2E' stroke-width='2' stroke-linecap='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")"
  }} data-id="element-871" data-mp="1dm4a54tjhcvr">
      <option value="" data-id="element-872" data-mp="21yvh1py6a9p">{placeholder}</option>
      {options.map(opt => <option key={opt.value} value={opt.value} data-id="element-873" data-mp="3gjfaajwsp3l">
          {opt.label}
        </option>)}
    </select>
  </FieldShell>;
interface RatingFieldProps {
  id: string;
  label: string;
  value: number | null;
  onChange: (value: number) => void;
  error?: string;
}
export const RatingField = ({
  id,
  label,
  value,
  onChange,
  error
}: RatingFieldProps) => <FieldShell id={id} label={label} error={error} data-id="element-874" data-mp="3gwh4ht8wpr0b">
    <div role="radiogroup" aria-label={label} aria-invalid={!!error} className="flex items-stretch gap-2 sm:gap-3" data-id="element-875" data-mp="3gtuu7lumsxee">
      {[1, 2, 3, 4, 5].map(n => {
      const selected = value === n;
      return <button key={n} type="button" role="radio" aria-checked={selected} onClick={() => onChange(n)} className={`flex-1 min-h-[52px] rounded-lg border font-body text-lg font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tns-gold/50 active:scale-[0.97] ${selected ? 'bg-tns-navy text-tns-gold border-tns-navy shadow-md' : 'bg-white text-tns-charcoal border-tns-ivory-dark hover:border-tns-gold/60'}`} data-id="element-876" data-mp="3vktzamw9d3t8">
            {n}
          </button>;
    })}
    </div>
    <div className="flex justify-between mt-2 font-body text-xs text-tns-charcoal-soft" data-id="element-877" data-mp="16brird0e1mj4">
      <span data-id="element-878" data-mp="11cewouhccjhl">Rarely</span>
      <span data-id="element-879" data-mp="wju7655gwsjv">Constantly</span>
    </div>
  </FieldShell>;
interface YesNoFieldProps {
  id: string;
  label: string;
  value: 'Yes' | 'No' | '';
  onChange: (value: 'Yes' | 'No') => void;
  error?: string;
}
export const YesNoField = ({
  id,
  label,
  value,
  onChange,
  error
}: YesNoFieldProps) => <FieldShell id={id} label={label} error={error} data-id="element-880" data-mp="ijjfizwc39xn">
    <div role="radiogroup" aria-label={label} aria-invalid={!!error} className="grid grid-cols-2 gap-3" data-id="element-881" data-mp="ijazx08ttqir">
      {(['Yes', 'No'] as const).map(option => {
      const selected = value === option;
      return <button key={option} type="button" role="radio" aria-checked={selected} onClick={() => onChange(option)} className={`min-h-[52px] rounded-lg border font-body font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tns-gold/50 active:scale-[0.98] ${selected ? 'bg-tns-navy text-tns-gold border-tns-navy shadow-md' : 'bg-white text-tns-charcoal border-tns-ivory-dark hover:border-tns-gold/60'}`} data-id="element-882" data-mp="8vxg0437gcsd">
            {option}
          </button>;
    })}
    </div>
  </FieldShell>;